// Dashboard event stream — one data pipeline for every surface that needs to
// know "what happened on the dashboard". Today it feeds the AI Command
// Center's live feed; the same subscribe/emit contract is the seam for future
// consumers (push-notification previews, badge counts, other pages) and,
// server-side, a DB-webhook fan-out into the send-push-notifications edge
// function using the same event shape (see utils/dashboardEvents.js).
//
// Flow: start() loads one snapshot (the classic dashboard's channels), builds
// the normalized feed, then keeps it live two ways:
//   1. Immediate — postgres_changes on price_history / count_sessions /
//      purchase_orders are mapped straight to feed events (Slack-style push).
//   2. Reconciled — any change also schedules a debounced full reload so
//      derived state (needs-attention cards, tool statuses) stays correct.
// Every event is additionally dispatched as a window CustomEvent
// ('dashboard:event') for non-React consumers.

import supabase from '../config/supabase';
import {
  buildDashboardFeed, sortFeed, localTodayIso,
  priceChangeEvent, sessionEvents, orderSentEvent,
} from '../utils/dashboardEvents';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const RELOAD_DEBOUNCE_MS = 1200;

const withTimeout = (promise, ms, fallback = null) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
]);

/**
 * One snapshot of every classic-dashboard channel. Compared to the version
 * that used to live in AICommandCenter: sessions carry created_at, vendors
 * carry ordering schedules (for order_upcoming events), and Countertop
 * messages come back in full (the feed renders them, not just a count).
 */
export async function loadCommandCenterData(storeId, client = supabase) {
  const since30 = new Date(Date.now() - THIRTY_DAYS_MS).toISOString();
  const [
    sessionsRes, vendorsRes, itemsRes, actionableRes, sentRes, priceRes,
    setupRes, invoicesRes, messagesRes,
  ] = await Promise.allSettled([
    client.from('count_sessions')
      .select('id,name,status,session_date,created_at,started_at,completed_at,updated_at')
      .eq('store_id', storeId)
      .order('session_date', { ascending: false })
      .limit(200),
    client.from('vendors')
      .select('id,display_name,name,contact_email,contact_phone,contact_name,ordering_days,ordering_schedule,cutoff_time')
      .eq('store_id', storeId)
      .eq('status', 'active'),
    client.from('inventory_items')
      .select('id', { count: 'exact', head: true })
      .eq('store_id', storeId)
      .eq('is_active', true),
    client.from('purchase_orders')
      .select('id', { count: 'exact', head: true })
      .eq('store_id', storeId)
      .in('delivery_status', ['draft', 'blocked_no_email', 'bounced', 'complained', 'error', 'failed', 'blocked', 'delivery_delayed'])
      .eq('is_latest_revision', true)
      .is('archived_at', null),
    client.from('purchase_orders')
      .select('id,vendor_name_snapshot,sent_at')
      .eq('store_id', storeId)
      .not('sent_at', 'is', null)
      .order('sent_at', { ascending: false })
      .limit(10),
    client.from('price_history')
      .select('id,changed_at,old_case_price,new_case_price,invoice_vendor,inventory_item_id,change_type,new_name,old_name')
      .eq('store_id', storeId)
      .eq('change_type', 'updated')
      .gte('changed_at', since30)
      .order('changed_at', { ascending: false })
      .limit(400),
    withTimeout(
      client.functions.invoke('setup-progress', { body: { action: 'progress', store_id: storeId } }),
      8000,
    ),
    withTimeout(
      client.functions.invoke('store-invoice-processor', { body: { action: 'get_inbox', storeId } }),
      8000,
    ),
    withTimeout(client.rpc('get_active_messages_with_read_state'), 8000),
  ]);

  const ok = (r) => (r.status === 'fulfilled' && r.value && !r.value.error ? r.value : null);

  const sessions = ok(sessionsRes)?.data || [];
  const vendors = ok(vendorsRes)?.data || [];
  const itemCount = ok(itemsRes)?.count ?? null;
  const actionablePOs = ok(actionableRes)?.count ?? 0;
  const sentPOs = ok(sentRes)?.data || [];
  const priceRows = ok(priceRes)?.data || [];
  const setupPayload = ok(setupRes)?.data || null;
  const invoiceHistory = (ok(invoicesRes)?.data?.history || []).filter((row) => {
    const ts = Date.parse(row.received_at || row.processed_at || '');
    return Number.isFinite(ts) && ts >= Date.now() - THIRTY_DAYS_MS;
  });
  const messages = ok(messagesRes)?.data || [];

  return {
    sessions, vendors, itemCount, actionablePOs, sentPOs, priceRows,
    setupPayload, invoiceHistory, messages,
  };
}

/**
 * createDashboardEventStream({ storeId, client }) →
 *   start()    — load snapshot, emit feed, open the realtime channel.
 *                Resolves with the snapshot data (also pushed to subscribers).
 *   stop()     — tear down channel/timers.
 *   subscribe(fn) → fn(events, data) on every emission; returns unsubscribe.
 *   getEvents()/getData() — current snapshot of each.
 *   drop(id)   — optimistic local removal (message "✓ Got it"); the next
 *                reload reconciles with the server.
 */
export function createDashboardEventStream({ storeId, client = supabase } = {}) {
  const listeners = new Set();
  let events = [];
  let data = null;
  let channel = null;
  let reloadTimer = null;
  let stopped = false;

  const notify = () => {
    listeners.forEach((fn) => {
      try { fn(events, data); } catch (_) { /* listener errors stay isolated */ }
    });
  };

  const announceLive = (event) => {
    if (typeof window === 'undefined') return;
    try { window.dispatchEvent(new CustomEvent('dashboard:event', { detail: event })); } catch (_) { /* non-DOM env */ }
  };

  const upsertLive = (fresh) => {
    const list = (Array.isArray(fresh) ? fresh : [fresh]).filter(Boolean);
    if (!list.length || stopped) return;
    const keep = events.filter((e) => !list.some((f) => f.id === e.id));
    events = sortFeed([...list, ...keep]);
    notify();
    list.forEach(announceLive);
  };

  const scheduleReload = () => {
    if (stopped) return;
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      start().catch(() => { /* keep the last good snapshot */ });
    }, RELOAD_DEBOUNCE_MS);
  };

  const onPriceHistory = (payload) => {
    if (payload?.eventType === 'INSERT' && payload.new?.change_type === 'updated') {
      upsertLive(priceChangeEvent(payload.new, { live: true }));
    }
    scheduleReload();
  };

  const onCountSession = (payload) => {
    const row = payload?.new;
    if (row && (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE')) {
      upsertLive(sessionEvents(row, { live: true }));
    }
    scheduleReload();
  };

  const onPurchaseOrder = (payload) => {
    if (payload?.eventType === 'UPDATE' && payload.new?.sent_at) {
      upsertLive(orderSentEvent(payload.new, { live: true }));
    }
    scheduleReload();
  };

  const subscribeRealtime = () => {
    if (channel || stopped) return;
    const filter = `store_id=eq.${storeId}`;
    try {
      channel = client
        .channel(`dashboard-feed-${storeId}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'price_history', filter }, onPriceHistory)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'count_sessions', filter }, onCountSession)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'purchase_orders', filter }, onPurchaseOrder)
        .subscribe();
    } catch (_) {
      channel = null; // snapshot-only mode: the feed still works on load
    }
  };

  async function start() {
    const loaded = await loadCommandCenterData(storeId, client);
    if (stopped) return loaded;
    data = loaded;
    events = buildDashboardFeed(loaded, { todayIso: localTodayIso() });
    notify();
    subscribeRealtime();
    return loaded;
  }

  function stop() {
    stopped = true;
    if (reloadTimer) { clearTimeout(reloadTimer); reloadTimer = null; }
    if (channel) {
      try { client.removeChannel(channel); } catch (_) { /* already gone */ }
      channel = null;
    }
    listeners.clear();
  }

  return {
    start,
    stop,
    subscribe(fn) {
      listeners.add(fn);
      try { fn(events, data); } catch (_) { /* ignore */ }
      return () => listeners.delete(fn);
    },
    getEvents: () => events,
    getData: () => data,
    drop(id) {
      const next = events.filter((e) => e.id !== id);
      if (next.length === events.length) return;
      events = next;
      notify();
    },
  };
}
